import { Dialog } from '@headlessui/react';
import { useFormikContext } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { PartsItem } from './PartsTable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  partIndex: number | null;
}

export const EditPartModal = ({ isOpen, onClose, partIndex }: Props) => {
  const { values, setFieldValue } = useFormikContext<{ parts: PartsItem[] }>();
  const currentPart = partIndex !== null ? values.parts[partIndex] : null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const updatedPart = {
      request_type: formData.get('request_type') as string,
      part_number: formData.get('part_number') as string,
      part_description: formData.get('part_description') as string,
    };

    if (partIndex !== null) {
      const newParts = [...values.parts];
      newParts[partIndex] = updatedPart;
      setFieldValue('parts', newParts);
    }

    onClose();
  };

  return (
    <AnimatePresence>
      <Dialog
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="relative z-10"
        onClose={onClose}
        open={isOpen}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl w-full max-w-lg"
            >
              <form onSubmit={handleSave}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="request_type"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Request Type
                    </label>
                    <input
                      type="text"
                      name="request_type"
                      id="request_type"
                      defaultValue={currentPart?.request_type}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="part_number"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Part Number
                    </label>
                    <input
                      type="text"
                      name="part_number"
                      id="part_number"
                      defaultValue={currentPart?.part_number}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="part_description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Part Description
                    </label>
                    <input
                      type="text"
                      name="part_description"
                      id="part_description"
                      defaultValue={currentPart?.part_description}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </Dialog>
    </AnimatePresence>
  );
};
